import {
  AccessNotice,
  SourceAccountBadge,
} from "./payrollProfilePresentation.js";
import { SelectField } from "./payrollProfileForms.js";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { Link } from "../lib/navigation.js";

import { useAuth } from "../app/auth.js";
import { ScopedRefreshControl } from "../components/payroll/ScopedRefreshControl.js";
import {
  Badge,
  Button,
  Card,
  CompactTable,
  EmployeeIdentity,
  Input,
  Skeleton,
} from "../components/ui/index.js";
import { api, type OpenProjectPayrollProfilesResponse } from "../lib/api.js";
import { queryClient } from "../lib/queryClient.js";
import { refreshScopedSourceQueries } from "../lib/scopedRefresh.js";

import {
  PROFILE_DIRECTORY_PAGE_SIZE,
  type PayrollUser,
  type ProfileFilter,
  contractLabel,
  currentMonth,
  directoryType,
  employeeName,
  errorMessage,
  periodSubtitle,
  sourceActivity,
  sourceActivityLabel,
  sourceActivityOrder,
} from "./payrollProfileModel.js";

export const PayrollProfilesPage = () => {
  const authQuery = useAuth();
  const currentUser = authQuery.data?.user;
  const payrollAccessQuery = useQuery<{ officeAccess: boolean }>({
    queryKey: ["openproject", "payroll-access"],
    queryFn: () => api.getOpenProjectPayrollAccess(),
    enabled: Boolean(currentUser),
    retry: false,
  });
  const hasPayrollAccess = Boolean(payrollAccessQuery.data?.officeAccess);
  const eligibilityMonth = currentMonth();
  const profilesQuery = useQuery<
    OpenProjectPayrollProfilesResponse,
    Error,
    { users: PayrollUser[] }
  >({
    queryKey: ["openproject", "payroll-profiles", "directory"],
    queryFn: () => api.getOpenProjectPayrollProfiles(),
    enabled: Boolean(currentUser && hasPayrollAccess),
    refetchOnMount: "always",
  });
  const directorySearch =
    typeof window === "undefined"
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
  const initialFilter = directorySearch.get("profileFilter");
  const [search, setSearch] = useState(
    () => directorySearch.get("profileSearch") ?? "",
  );
  const [filter, setFilter] = useState<ProfileFilter>(() =>
    initialFilter === "employees" ||
    initialFilter === "freelancers" ||
    initialFilter === "students" ||
    initialFilter === "others"
      ? initialFilter
      : "all",
  );
  const [page, setPage] = useState(() => {
    const candidate = Number.parseInt(
      directorySearch.get("profilePage") ?? "1",
      10,
    );
    return Number.isFinite(candidate) && candidate > 0 ? candidate : 1;
  });
  const [feedback] = useState<{
    tone: "success" | "error";
    message: string;
  } | null>(null);
  const allUsers = useMemo(
    () =>
      [...(profilesQuery.data?.users ?? [])].sort(
        (a, b) =>
          sourceActivityOrder[sourceActivity(a)] -
            sourceActivityOrder[sourceActivity(b)] ||
          employeeName(a).localeCompare(employeeName(b)),
      ),
    [profilesQuery.data?.users],
  );
  const filteredUsers = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    return allUsers.filter((user) => {
      const searchable =
        `${employeeName(user)} ${user.email} ${user.username} ${user.payrollEmployeeCode ?? ""}`.toLocaleLowerCase();
      const matchesSearch = !term || searchable.includes(term);
      const matchesFilter =
        filter === "all" ||
        (filter === "employees" && directoryType(user) === "employee") ||
        (filter === "freelancers" && directoryType(user) === "freelancer") ||
        (filter === "students" &&
          directoryType(user) === "student_agreement") ||
        (filter === "others" && directoryType(user) === "other");
      return matchesSearch && matchesFilter;
    });
  }, [allUsers, filter, search]);
  const pageCount = Math.max(
    1,
    Math.ceil(filteredUsers.length / PROFILE_DIRECTORY_PAGE_SIZE),
  );
  const boundedPage = Math.min(page, pageCount);
  const pageStart = (boundedPage - 1) * PROFILE_DIRECTORY_PAGE_SIZE;
  const visibleUsers = filteredUsers.slice(
    pageStart,
    pageStart + PROFILE_DIRECTORY_PAGE_SIZE,
  );

  useEffect(() => {
    if (page !== boundedPage) {
      setPage(boundedPage);
      return;
    }
    const next = new URL(window.location.href);
    for (const key of ["month", "stage", "payslipStep", "periodThrough"])
      next.searchParams.delete(key);
    if (search) next.searchParams.set("profileSearch", search);
    else next.searchParams.delete("profileSearch");
    if (filter !== "all") next.searchParams.set("profileFilter", filter);
    else next.searchParams.delete("profileFilter");
    if (boundedPage > 1)
      next.searchParams.set("profilePage", String(boundedPage));
    else next.searchParams.delete("profilePage");
    window.history.replaceState(
      window.history.state,
      "",
      `${next.pathname}${next.search}${next.hash}`,
    );
  }, [boundedPage, filter, page, search]);

  const updateSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };
  const updateFilter = (value: ProfileFilter) => {
    setFilter(value);
    setPage(1);
  };

  if (authQuery.isLoading || (currentUser && payrollAccessQuery.isLoading)) {
    return (
      <Card elevated>
        <div
          className="space-y-3"
          role="status"
          aria-label="Checking payroll profile access"
        >
          <Skeleton className="h-5 w-52 rounded-lg" />
          <Skeleton className="h-10 w-full rounded-xl" />
          <Skeleton className="h-16 w-full rounded-xl" />
        </div>
      </Card>
    );
  }
  if (!currentUser) {
    return (
      <AccessNotice title="Sign in required">
        Sign in before opening Work profiles.
      </AccessNotice>
    );
  }
  if (payrollAccessQuery.isError || !hasPayrollAccess) {
    return (
      <AccessNotice title="Payroll access required">
        OpenProject payroll office access is required to manage Work profiles.
      </AccessNotice>
    );
  }

  return (
    <div
      className="payroll-profile-ledger payroll-profile-ledger--overview space-y-3"
      data-page-layout="fiori-floorplan-profiles"
    >
      <h1 className="sr-only">Work profiles</h1>

      <Card elevated padding="sm">
        <div className="grid items-end gap-2 sm:grid-cols-[minmax(0,1fr)_180px_auto]">
          <Input
            id="profile-search"
            type="search"
            aria-label="Search employees"
            placeholder="Search employees"
            value={search}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              updateSearch(event.target.value)
            }
            leftIcon={<Search aria-hidden="true" size={16} />}
          />
          <SelectField
            id="profile-filter"
            label="Profile status"
            value={filter}
            onChange={(value) => updateFilter(value as ProfileFilter)}
          >
            <option value="all">All profiles</option>
            <option value="employees">Employees</option>
            <option value="freelancers">Freelancers</option>
            <option value="students">Students</option>
            <option value="others">Others</option>
          </SelectField>
          <ScopedRefreshControl
            scope="work_profiles"
            label="Refresh work profiles"
            onDone={() =>
              refreshScopedSourceQueries(
                queryClient,
                "work_profiles",
                eligibilityMonth,
              )
            }
          />
        </div>
        {feedback && (
          <p
            className={`mt-3 rounded-xl px-3 py-2 text-sm ${feedback.tone === "success" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}
            role="status"
          >
            {feedback.message}
          </p>
        )}
        <div className="my-2 text-xs">
          <div>
            <p className="mt-1 text-xs text-foreground-muted">
              {filteredUsers.length === 0
                ? `0 of ${allUsers.length} employees`
                : `${pageStart + 1}–${Math.min(pageStart + PROFILE_DIRECTORY_PAGE_SIZE, filteredUsers.length)} of ${filteredUsers.length}${filteredUsers.length === allUsers.length ? "" : ` (filtered from ${allUsers.length})`} employees`}{" "}
            </p>
          </div>
        </div>
        {profilesQuery.isLoading && (
          <div
            className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3"
            role="status"
            aria-label="Loading Work profiles"
          >
            {[0, 1, 2, 3, 4, 5].map((card) => (
              <Skeleton key={card} className="h-52 rounded-2xl" />
            ))}
          </div>
        )}
        {profilesQuery.isError && (
          <p
            className="m-4 rounded-xl bg-danger-subtle px-4 py-3 text-sm text-danger"
            role="alert"
          >
            {errorMessage(profilesQuery.error)}
          </p>
        )}
        {!profilesQuery.isLoading && !profilesQuery.isError && (
          <div
            data-responsive-contract="work-profiles-directory"
            className="min-w-0"
          >
            <CompactTable
              label="Work profiles"
              headers={[
                { label: "Name" },
                { label: "Type" },
                { label: "Status", align: "right" },
              ]}
            >
              {visibleUsers.map((user) => (
                <tr key={user.id} className="hover:bg-elevated/35">
                  <td className="px-4 py-2 align-middle">
                    <Link
                      data-payroll-profile-row="true"
                      to={`/work-profiles/${user.id}`}
                      aria-label={`${employeeName(user)}. ${user.email || user.username}. OpenProject account: ${sourceActivityLabel[sourceActivity(user)]}. Import: ${user.isActive ? "included" : "excluded"}. Open payroll profile.`}
                      className="block rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                    >
                      <EmployeeIdentity
                        name={employeeName(user)}
                        subtitle={periodSubtitle(user)}
                      />
                    </Link>
                  </td>
                  <td className="px-4 py-2 align-middle">
                    <Badge variant="outline">
                      {directoryType(user)
                        ? contractLabel(directoryType(user)!)
                        : "No applicable period"}
                    </Badge>
                  </td>
                  <td className="px-4 py-2 align-middle text-right">
                    <SourceAccountBadge user={user} />
                  </td>
                </tr>
              ))}
            </CompactTable>
            {filteredUsers.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-foreground-muted">
                No matching employees. Try another name or profile type.
              </p>
            )}
          </div>
        )}
        {!profilesQuery.isLoading &&
          !profilesQuery.isError &&
          filteredUsers.length > PROFILE_DIRECTORY_PAGE_SIZE && (
            <nav
              className="flex items-center justify-between gap-3 border-t border-border px-5 py-3"
              aria-label="Work profile pages"
            >
              <p className="text-xs text-foreground-muted">
                Page {boundedPage} of {pageCount}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={boundedPage === 1}
                  onClick={() => setPage(boundedPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={boundedPage === pageCount}
                  onClick={() => setPage(boundedPage + 1)}
                >
                  Next
                </Button>
              </div>
            </nav>
          )}
      </Card>
    </div>
  );
};
